import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { Slide, ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { yearlyReportsManagement } from "../../../../../../hooks/api/YearlyReportsManagement/YearlyReportsManagement";
import { languagesManagement } from "../../../../../../hooks/api/LanguagesManagement";
import { languageProgramsManagement } from "../../../../../../hooks/api/LanguageProgramsManagement";
import { prepareSelect } from "../../../../../../services/core/Select";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import { Select } from "../../../../../../themes/Default/components/Form/components/Select";
import { Textarea } from "../../../../../../themes/Default/components/Form/components/Textarea";

const EditSemiyearly = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { reportId } = useParams();
    const [formErrors, setFormErrors] = useState([]);
    const { useUpdateYearlyReportsMutation, useViewYearlyReportsMutation} = yearlyReportsManagement;
    const [updateReport, {isLoading: isUpdateingReport}] = useUpdateYearlyReportsMutation();
    const [viewReport] = useViewYearlyReportsMutation();
    const { useLanguageListMutation } = languagesManagement;
    const [languageList] = useLanguageListMutation();
    const [languages, setLanguages] = useState([]);
    const { useProgramListMutation } = languageProgramsManagement;
    const [languageProgram, setLanguageProgram] = useState([]);
    const [languageProgramList] = useProgramListMutation();

    const tabs = [
        { title: t('reports.montlhy'), onClick: () => gotoMonthlyTab()},
        { title: t('reports.semiAnnual'), onClick: () => gotoSemiyearlyTab()},
        { title: t('reports.annual'), isActive: true},
    ];

    const gotoMonthlyTab = () => {
        navigate("/settings/reports/monthly");
    }

    const gotoSemiyearlyTab = () => {
        navigate("/settings/reports/semiyearly");
    }

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = {...values};
        updateReport({body, reportId}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                navigate("/settings/reports/yearly");
                toast.info(result?.data?.info?.responseMessage);
            }
        });
    };

    useEffect(() => {
        viewReport(reportId).then((result) => {
            formRef.current.setValues(result?.data);
            console.log(result?.data);

            languageProgramList({language: result?.data?.language}).then((result) => {
                setLanguageProgram(prepareSelect(result?.data?.records));
            });
        });

    }, []);

    useEffect(() => {
        languageList().then((result) => {
            setLanguages(prepareSelect(result?.data?.records));
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings/reports/yearly" />
                <Button title={t('save')} isDisabled={isUpdateingReport} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("reports.title")} icon="fa-gear" />
                    <Link title={t("reports.annual")} />
                    <Link title={t("reports.update")} isActive={true} />
                </Breadcrumps>
                    <Formik
                        innerRef={formRef}
                        initialValues={{
                            title: "",
                            classNumber: null,
                            description: "",
                            language: null,
                            languageProgram: null
                        }}
                        onSubmit={onSaved}
                    >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Input
                                        name="title"
                                        label={t("reports.name")}
                                        required={true}
                                        value={values.title}
                                        onChange={handleChange}
                                        error={formErrors?.title}
                                    />
                                </Field>  
                                <Field>
                                    <Input
                                        name="classNumber"
                                        label={t("reports.classNumber")}
                                        required={true}
                                        value={values.classNumber}
                                        onChange={handleChange}
                                        error={formErrors?.classNumber}
                                    />
                                </Field>                         
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        rows={5}
                                        name="description"
                                        label={t("reports.description")}
                                        value={values.description}
                                        onChange={handleChange}
                                        error={formErrors?.description}
                                    />
                                </Field>                               
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="language"
                                        label={t("program.language")}
                                        required={true}
                                        options={languages}
                                        onChange={(event) => {
                                            setFieldValue('language', event.target.value);
                                            languageProgramList({language: event.target.value}).then((result) => {
                                                setLanguageProgram(prepareSelect(result?.data?.records));
                                            });
                                        }}
                                        selectedValue={values.language}
                                        error={formErrors?.language}
                                    />
                                </Field> 
                                <Field>
                                    <Select 
                                        name="languageProgram" 
                                        label={t('groups.languageProgram')} 
                                        onChange={handleChange} 
                                        required={true}
                                        options={languageProgram} 
                                        selectedValue={values.languageProgram} 
                                        error={formErrors?.program}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditSemiyearly;